import { get, post, put, del } from "../../../services/axiosUitils";

const MEDICATION_CRUD_URL = "/food_scanner/medication/crud/";
const MEDICATION_DONE_URL = "/food_scanner/medication/done/";
const SILENT = { showNotification: false };

// API Response Types
export interface ApiSuccessResponse {
  code: string;
  message: string;
}

export interface AddMedicationResponse {
  success?: ApiSuccessResponse;
  message?: string;
  timing_id?: string;
  medication_id?: string;
  time?: string;
  frequency?: string;
  status?: "not_taken" | "taken" | string;
  timings?: Array<Record<string, unknown>>;
}

export interface MarkDoneResponse {
  success?: ApiSuccessResponse;
  message?: string;
  timing_id?: string;
  medication_id?: string;
  medication_name?: string;
  time?: string;
  display_time?: string;
  date?: string;
  frequency?: string;
  taken_at?: string;
  scheduled_at?: string;
  diff_minutes?: number;
  adherence?: MedicationAdherence;
  diff_label?: string;
  status?: "taken" | string;
  medications?: Array<Record<string, unknown>>;
}

export type MedicationRepeatEndType = "never" | "on_date" | "after_doses";

// Request Types — POST /food_scanner/medication/crud/
export interface AddMedicationRequest {
  medication_name: string;
  /** Single time (HH:MM) — used when only one slot */
  time?: string;
  /** Multiple times in one request (new API) */
  times?: string[];
  frequency: string;
  repeat_enabled?: boolean;
  repeat_days?: string[];
  repeat_end_type?: MedicationRepeatEndType;
  end_date?: string;
  number_of_doses?: number;
}

export interface MarkDoneRequest {
  timing_id: string;
}

export interface MedicationReminder {
  "Timing ID": string;
  "Medication ID": string;
  "Medication Name": string;
  "Medication Time": string;
  "Full Time String": string;
  Status: string;
  Frequency: string;
}

export type MedicationAdherence = "early" | "on_time" | "overdue" | string;

export interface LocalMedication {
  timing_id: string;
  medication_id: string;
  medication_name: string;
  time: string;
  frequency: string;
  status: "not_taken" | "taken" | string;
  created_at?: string;
  date?: string;
  display_time?: string;
  taken_at?: string;
  scheduled_at?: string;
  diff_minutes?: number;
  adherence?: MedicationAdherence;
  diff_label?: string;
  repeat_enabled?: boolean;
  repeat_days?: string[];
  repeat_end_type?: MedicationRepeatEndType;
  end_date?: string | null;
  number_of_doses?: number | null;
}

function normalizeTime(time?: string): string {
  if (!time) return "";
  const parts = time.trim().split(":");
  if (parts.length >= 2) {
    return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
  }
  return time.trim();
}

function normalizeStatus(raw?: string): "not_taken" | "taken" {
  const value = (raw ?? "").toLowerCase();
  if (value === "taken" || value === "done" || value === "completed") {
    return "taken";
  }
  return "not_taken";
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function mapTimingRecord(
  raw: Record<string, unknown>,
  parent?: Record<string, unknown>,
): LocalMedication | null {
  const source = parent ?? raw;
  const timingId = String(
    raw.timing_id ?? raw["Timing ID"] ?? raw.id ?? "",
  ).trim();
  if (!timingId) return null;

  return {
    timing_id: timingId,
    medication_id: String(
      raw.medication_id ?? source.medication_id ?? source["Medication ID"] ?? "",
    ),
    medication_name: String(
      raw.medication_name ??
        source.medication_name ??
        source["Medication Name"] ??
        "",
    ),
    time: normalizeTime(
      String(
        raw.time ??
          raw.medication_time ??
          raw["Medication Time"] ??
          source.time ??
          "",
      ),
    ),
    frequency: String(
      raw.frequency ?? source.frequency ?? source.Frequency ?? "",
    ),
    status: normalizeStatus(String(raw.status ?? raw.Status ?? "not_taken")),
    created_at:
      typeof raw.created_at === "string" ? raw.created_at : undefined,
    repeat_enabled:
      typeof source.repeat_enabled === "boolean"
        ? source.repeat_enabled
        : undefined,
    repeat_days: Array.isArray(source.repeat_days)
      ? (source.repeat_days as string[])
      : undefined,
    repeat_end_type: source.repeat_end_type as
      | MedicationRepeatEndType
      | undefined,
    end_date:
      typeof source.end_date === "string" ? source.end_date : null,
    number_of_doses:
      typeof source.number_of_doses === "number"
        ? source.number_of_doses
        : null,
    date: typeof raw.date === "string" ? raw.date : undefined,
    display_time:
      typeof raw.display_time === "string"
        ? normalizeTime(raw.display_time)
        : undefined,
    taken_at: typeof raw.taken_at === "string" ? raw.taken_at : undefined,
    scheduled_at:
      typeof raw.scheduled_at === "string" ? raw.scheduled_at : undefined,
    diff_minutes:
      typeof raw.diff_minutes === "number" ? raw.diff_minutes : undefined,
    adherence:
      typeof raw.adherence === "string" ? raw.adherence : undefined,
    diff_label:
      typeof raw.diff_label === "string" ? raw.diff_label : undefined,
  };
}

function flattenMedicationItem(item: unknown): LocalMedication[] {
  const rec = asRecord(item);
  if (!rec) return [];

  const nestedTimings = rec.timings ?? rec.medication_timings;
  if (Array.isArray(nestedTimings)) {
    return nestedTimings
      .map((timing) => mapTimingRecord(asRecord(timing) ?? {}, rec))
      .filter((med): med is LocalMedication => med !== null);
  }

  const flat = mapTimingRecord(rec);
  return flat ? [flat] : [];
}

/** Normalize GET /medication/crud/ — supports {}, [], nested medications/timings */
export function parseMedicationListResponse(
  response: unknown,
): LocalMedication[] {
  if (!response) return [];
  if (Array.isArray(response)) {
    return response.flatMap((item) => flattenMedicationItem(item));
  }

  const root = asRecord(response);
  if (!root) return [];
  if (Object.keys(root).length === 0) return [];

  const listKeys = [
    "medications",
    "medication_timings",
    "timings",
    "results",
    "data",
  ] as const;

  for (const key of listKeys) {
    const value = root[key];
    if (Array.isArray(value)) {
      return value.flatMap((item) => flattenMedicationItem(item));
    }
    const nested = asRecord(value);
    if (nested) {
      const parsed = parseMedicationListResponse(nested);
      if (parsed.length > 0) return parsed;
    }
  }

  if (root.timing_id || root["Timing ID"]) {
    const one = mapTimingRecord(root);
    return one ? [one] : [];
  }

  return [];
}

/** Merge GET/done API record onto an existing list item. */
export function patchMedicationFromApi(
  existing: LocalMedication,
  api: Record<string, unknown>,
): LocalMedication {
  const mapped = mapTimingRecord(api);
  if (!mapped) {
    return { ...existing, status: "taken" };
  }
  return { ...existing, ...mapped };
}

/** Normalize POST /medication/done/ into a list row. */
export function medicationFromDoneResponse(
  response: MarkDoneResponse | Record<string, unknown>,
  fallback?: LocalMedication,
): LocalMedication | null {
  const rec = asRecord(response);
  if (!rec) return fallback ?? null;

  const nestedMeds = rec.medications;
  if (Array.isArray(nestedMeds) && nestedMeds.length > 0) {
    const first = mapTimingRecord(asRecord(nestedMeds[0]) ?? {});
    if (first) {
      return fallback ? { ...fallback, ...first } : first;
    }
  }

  const mapped = mapTimingRecord(rec);
  if (!mapped && !fallback) return null;
  if (!mapped) {
    return { ...fallback!, status: "taken" };
  }
  return fallback ? { ...fallback, ...mapped } : mapped;
}

function successCode(response: unknown): string | undefined {
  const rec = asRecord(response);
  if (!rec) return undefined;
  const nested = asRecord(rec.success);
  const code = nested?.code ?? rec.code;
  return typeof code === "string" ? code : undefined;
}

function isEmptySuccessBody(response: unknown): boolean {
  const rec = asRecord(response);
  return rec !== null && Object.keys(rec).length === 0;
}

function isMutationSuccess(response: unknown): boolean {
  const rec = asRecord(response);
  if (!rec) return false;
  if (rec.timing_id || rec.medication_id) return true;
  if (Array.isArray(rec.timings) && rec.timings.length > 0) return true;

  const code = successCode(response)?.toLowerCase();
  return (
    code === "created" ||
    code === "success" ||
    code === "ok" ||
    code === "retrieved" ||
    code === "updated"
  );
}

function extractApiMessage(response: unknown): string | undefined {
  const rec = asRecord(response);
  if (!rec) return undefined;
  if (typeof rec.message === "string" && rec.message.trim()) {
    return rec.message;
  }
  const nested = asRecord(rec.error);
  if (typeof nested?.message === "string") return nested.message;
  const success = asRecord(rec.success);
  if (typeof success?.message === "string") return success.message;
  return undefined;
}

class MedicationService {
  private static instance: MedicationService;

  private constructor() {}

  public static getInstance(): MedicationService {
    if (!MedicationService.instance) {
      MedicationService.instance = new MedicationService();
    }
    return MedicationService.instance;
  }

  private buildAddPayload(
    medicationData: AddMedicationRequest,
  ): Record<string, unknown> {
    const time =
      medicationData.time?.trim() ??
      medicationData.times?.map((t) => t.trim()).find(Boolean) ??
      "";

    if (!time) {
      throw new Error("Time is required.");
    }

    const { time: _time, times: _times, ...rest } = medicationData;
    return { ...rest, time };
  }

  /**
   * POST /food_scanner/medication/crud/ — one timing per request (backend limit).
   */
  async addMedication(
    medicationData: AddMedicationRequest,
  ): Promise<AddMedicationResponse> {
    const payload = this.buildAddPayload(medicationData);
    const response = await post<AddMedicationResponse>(
      MEDICATION_CRUD_URL,
      payload,
      SILENT,
    );

    if (!response) {
      throw new Error("Failed to add medication");
    }

    if (!isMutationSuccess(response)) {
      throw new Error(
        extractApiMessage(response) ?? "Failed to add medication",
      );
    }

    return response;
  }

  /**
   * Multiple times → separate POST per time (backend does not accept times[]).
   */
  async addMedicationTimes(
    medicationData: Omit<AddMedicationRequest, "time" | "times"> & {
      times: string[];
    },
  ): Promise<AddMedicationResponse[]> {
    const times = medicationData.times.map((t) => t.trim()).filter(Boolean);
    if (!times.length) {
      throw new Error("At least one time is required.");
    }

    const results: AddMedicationResponse[] = [];
    for (const time of times) {
      const result = await this.addMedication({ ...medicationData, time });
      results.push(result);
    }
    return results;
  }

  /**
   * POST /food_scanner/medication/done/
   * Body: { "timing_id": "<uuid>" }
   */
  async markMedicationDone(
    timing_id: string,
    existing?: LocalMedication,
  ): Promise<LocalMedication> {
    const id = timing_id?.trim();
    if (!id) {
      throw new Error("Medication timing is required.");
    }

    const response = await post<MarkDoneResponse>(
      MEDICATION_DONE_URL,
      { timing_id: id },
      SILENT,
    );

    if (isEmptySuccessBody(response)) {
      return (
        existing
          ? { ...existing, status: "taken", timing_id: id }
          : {
              timing_id: id,
              medication_id: "",
              medication_name: "",
              time: "",
              frequency: "",
              status: "taken",
            }
      );
    }

    if (!response) {
      throw new Error("Failed to mark medication as done");
    }

    const status = String(response.status ?? "").toLowerCase();
    const merged = medicationFromDoneResponse(
      { ...response, timing_id: response.timing_id ?? id, status: "taken" },
      existing,
    );

    if (merged && (status === "taken" || isMutationSuccess(response))) {
      return merged;
    }

    throw new Error(
      extractApiMessage(response) ?? "Failed to mark medication as done",
    );
  }

  /**
   * GET /food_scanner/medication/crud/
   */
  async getAllMedications(): Promise<LocalMedication[]> {
    try {
      const response = await get<unknown>(MEDICATION_CRUD_URL, SILENT);
      return parseMedicationListResponse(response);
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 404 || status === 500) {
        console.warn("Medication list unavailable:", status);
      } else {
        console.error("getAllMedications error:", error);
      }
      return [];
    }
  }

  /**
   * PUT /food_scanner/medication/crud/
   */
  async updateMedication(
    timing_id: string,
    medicationData: Partial<AddMedicationRequest>,
  ): Promise<AddMedicationResponse> {
    const { times: _times, time, ...rest } = medicationData;
    const payload = {
      timing_id,
      ...rest,
      ...(time ? { time } : {}),
    };

    const response = await put<AddMedicationResponse>(
      MEDICATION_CRUD_URL,
      payload,
      SILENT,
    );

    if (!response) {
      throw new Error("Failed to update medication");
    }
    if (!isMutationSuccess(response)) {
      throw new Error(
        extractApiMessage(response) ?? "Failed to update medication",
      );
    }
    return response;
  }

  /**
   * DELETE /food_scanner/medication/crud/
   * Body: { "timing_id": "<uuid>" }
   */
  async deleteMedication(
    timing_id: string,
  ): Promise<{ success?: ApiSuccessResponse; message?: string }> {
    const id = timing_id?.trim();
    if (!id) {
      throw new Error("Medication timing is required.");
    }

    const response = await del<{
      success?: ApiSuccessResponse;
      message?: string;
    }>(MEDICATION_CRUD_URL, {
      ...SILENT,
      data: { timing_id: id },
    });

    if (isEmptySuccessBody(response)) {
      return { message: "Deleted" };
    }

    if (!response) {
      throw new Error("Failed to delete medication");
    }

    if (isMutationSuccess(response)) {
      return response;
    }

    throw new Error(
      extractApiMessage(response) ?? "Failed to delete medication",
    );
  }

  convertToLocalMedication(
    response: AddMedicationResponse,
    medicationName: string,
  ): LocalMedication {
    return {
      timing_id: response.timing_id ?? "",
      medication_id: response.medication_id ?? "",
      medication_name: medicationName,
      time: normalizeTime(response.time),
      frequency: response.frequency ?? "",
      status: normalizeStatus(response.status),
      created_at: new Date().toISOString(),
    };
  }

  validateTime(time: string): boolean {
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(time);
  }

  formatTimeForDisplay(time: string): string {
    const hhmm = normalizeTime(time);
    if (!this.validateTime(hhmm)) {
      return time.trim();
    }

    const [hours, minutes] = hhmm.split(":");
    const hour12 = parseInt(hours, 10) % 12 || 12;
    const ampm = parseInt(hours, 10) >= 12 ? "PM" : "AM";

    return `${hour12}:${minutes} ${ampm}`;
  }

  formatMedicationDateLabel(dateStr?: string): string {
    if (!dateStr?.trim()) return "";
    const parsed = new Date(`${dateStr.trim()}T12:00:00`);
    if (Number.isNaN(parsed.getTime())) return dateStr;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const day = new Date(parsed);
    day.setHours(0, 0, 0, 0);

    if (day.getTime() === today.getTime()) return "Today";

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (day.getTime() === yesterday.getTime()) return "Yesterday";

    return parsed.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      ...(parsed.getFullYear() !== today.getFullYear()
        ? { year: "numeric" as const }
        : {}),
    });
  }

  formatIsoDateTimeLabel(iso?: string): string {
    if (!iso?.trim()) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }

  getScheduledLine(med: LocalMedication): string {
    const dateLabel = this.formatMedicationDateLabel(med.date);
    const timeLabel = this.formatTimeForDisplay(
      med.display_time ?? med.time ?? "",
    );
    if (dateLabel && timeLabel) {
      return `Scheduled · ${dateLabel} · ${timeLabel}`;
    }
    if (dateLabel) return `Scheduled · ${dateLabel}`;
    if (timeLabel) return `Scheduled · ${timeLabel}`;
    return "";
  }

  getMedicationStatusLine(med: LocalMedication): string {
    const taken = med.status === "taken";
    const takenTime = this.formatIsoDateTimeLabel(med.taken_at);
    const takenDate = med.taken_at
      ? this.formatMedicationDateLabel(med.taken_at.slice(0, 10))
      : this.formatMedicationDateLabel(med.date);
    const fallbackTime = this.formatTimeForDisplay(
      med.display_time ?? med.time ?? "",
    );

    if (!taken) {
      const scheduled = this.getScheduledLine(med);
      return scheduled ? scheduled.replace("Scheduled", "Due") : `Due · ${fallbackTime}`;
    }

    const whenPart =
      takenDate && takenTime
        ? `${takenDate} · ${takenTime}`
        : takenTime || fallbackTime;

    if (med.diff_label?.trim()) {
      return `Taken ${whenPart} · ${med.diff_label}`;
    }
    if (med.adherence === "on_time") {
      return `Taken ${whenPart} · On schedule`;
    }
    if (med.adherence === "early") {
      return `Taken ${whenPart} · Early`;
    }
    if (med.adherence === "overdue") {
      return `Taken ${whenPart} · Overdue`;
    }
    return `Taken ${whenPart}`;
  }

  getAdherenceTone(
    med: LocalMedication,
  ): "due" | "on_time" | "early" | "overdue" {
    if (med.status !== "taken") return "due";
    const adherence = (med.adherence ?? "").toLowerCase();
    if (adherence === "early") return "early";
    if (adherence === "overdue") return "overdue";
    return "on_time";
  }

  getFrequencyOptions(): string[] {
    return [
      "Once daily",
      "Twice daily",
      "Three times daily",
      "Before breakfast",
      "After breakfast",
      "Before lunch",
      "After lunch",
      "Before dinner",
      "After dinner",
      "Before bedtime",
      "As needed",
    ];
  }
}

const medicationService = MedicationService.getInstance();
export default medicationService;
